import { Icons } from "@app/shared";
import cn from "classnames";
import { FC, MouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type ImageLightboxProps = {
    show: boolean;
    src?: string;
    alt?: string;
    onClose: () => void;
};

/** Share of the viewport the fitted image may occupy, leaving a margin for the backdrop/close button. */
const FIT_FRACTION = 0.95;

/**
 * Full-screen image viewer.
 *
 * Opens with the image scaled to fit the viewport — scaled UP as well as down, so a still that is
 * smaller than the screen (most device snapshots on a large or 4K display) fills it rather than
 * sitting at its native size in the middle. Clicking the image zooms in a further step (double the
 * fitted size, or native 1:1 if that is larger) with scroll/pan, centred on the point that was
 * clicked; clicking again returns to fit. Click the backdrop, the close button, or press Escape to
 * dismiss.
 *
 * Portals straight to document.body with a high z-index so it layers above dialogs/modals.
 */
export const ImageLightbox: FC<ImageLightboxProps> = ({ show, src, alt, onClose }) => {
    const [zoomed, setZoomed] = useState(false);
    const [natural, setNatural] = useState<{ width: number; height: number }>();
    const [viewport, setViewport] = useState(() => ({ width: window.innerWidth, height: window.innerHeight }));
    const containerRef = useRef<HTMLDivElement>(null);
    // Where on the image (0..1 in each axis) the user clicked to zoom, so the zoomed view lands there.
    const anchorRef = useRef({ x: 0.5, y: 0.5 });

    // Always start fit-to-screen when a new image opens, and re-measure it (a cached image may not
    // fire onLoad again, so the size is also read off the element when it mounts).
    useEffect(() => {
        setZoomed(false);
        setNatural(undefined);
    }, [src, show]);

    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener("keydown", onKey);
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("resize", onResize);
        };
    }, [show, onClose]);

    const fitScale = natural ? Math.min((viewport.width * FIT_FRACTION) / natural.width, (viewport.height * FIT_FRACTION) / natural.height) : undefined;
    // Zoom is always a real step up from fit, so the click visibly does something even when the
    // image is already being shown at (or above) its native size.
    const zoomScale = fitScale !== undefined ? Math.max(1, fitScale * 2) : undefined;
    const scale = zoomed ? zoomScale : fitScale;
    const size = natural && scale !== undefined ? { width: natural.width * scale, height: natural.height * scale } : undefined;

    // After zooming in, scroll so the clicked point sits in the middle of the viewport instead of
    // the browser's default of snapping to the top-left corner.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!zoomed || !el || !size) return;
        el.scrollLeft = anchorRef.current.x * size.width - el.clientWidth / 2;
        el.scrollTop = anchorRef.current.y * size.height - el.clientHeight / 2;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoomed, size?.width, size?.height]);

    // Stable identity so React doesn't detach/re-attach the ref on every render; it is also the
    // onLoad handler, which covers an image that was already cached when the element mounted.
    const measure = useCallback(
        (img: HTMLImageElement | null) => {
            if (img && img.naturalWidth && img.naturalHeight && !natural) {
                setNatural({ width: img.naturalWidth, height: img.naturalHeight });
            }
        },
        [natural]
    );

    if (!show || !src) return null;

    const handleImageClick = (e: MouseEvent<HTMLImageElement>) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        anchorRef.current = {
            x: rect.width ? (e.clientX - rect.left) / rect.width : 0.5,
            y: rect.height ? (e.clientY - rect.top) / rect.height : 0.5,
        };
        setZoomed((z) => !z);
    };

    return createPortal(
        // `flex` + `m-auto` on the image (rather than justify/align-center) keeps a zoomed image that
        // overflows the viewport fully scrollable — flex centring would push its left/top edge out of
        // reach — while still centring one that is smaller than the viewport in either axis.
        <div ref={containerRef} role="dialog" aria-modal="true" aria-label={alt || "Image"} className="fixed inset-0 z-[1000] flex overflow-auto bg-black/80" onClick={onClose}>
            <button className="fixed top-4 right-4 z-[1001] rounded-full bg-brand-primary p-1.5 text-white hover:bg-brand-primary-lightened focus:outline-none" onClick={onClose} aria-label="Close">
                <Icons.Times size={24} />
            </button>
            <img
                ref={measure}
                src={src}
                alt={alt ?? ""}
                onLoad={(e) => measure(e.currentTarget)}
                onClick={handleImageClick}
                style={size}
                className={cn(
                    "m-auto block shrink-0",
                    zoomed ? "cursor-zoom-out" : "cursor-zoom-in",
                    // Until the natural size is known, fall back to CSS fitting so nothing flashes oversized.
                    !size && "max-h-[95vh] max-w-[95vw] object-contain"
                )}
            />
        </div>,
        document.body
    );
};
